import { createFileRoute, Outlet, useLocation, useRouter } from '@tanstack/react-router'
import { useEffect } from 'react'
import { AppLayout } from '@/components/app/layout'
import { SignedIn } from '@/components/auth/signed-in'
import { useSession } from '@/lib/auth-client'
import { shouldRedirectOperatorToSetup } from '@/lib/operator-config/launch-state'
import { useOperatorConfiguration } from '@/lib/operator-config/use-operator-configuration'
import type { RouteStaticData } from '@/lib/router/breadcrumbs'

export const Route = createFileRoute('/_app')({
  component: AppLayoutRoute,
  staticData: {
    breadcrumbs: [{ label: 'Home', href: '/' }],
  } satisfies RouteStaticData,
})

function AppLayoutRoute() {
  const router = useRouter()
  const location = useLocation()
  const { data: session, isPending } = useSession()
  const {
    launchState,
    isLoading: isOnboardingLoading,
    error: onboardingError,
  } = useOperatorConfiguration()

  const userRole = (session?.user as { role?: string } | undefined)?.role
  const isAdmin = userRole === 'admin'
  const isSetupRoute = location.pathname === '/setup'

  useEffect(() => {
    if (!isPending && !session) {
      router.navigate({ to: '/auth/$authView', params: { authView: 'sign-in' } })
    }
  }, [isPending, router, session])

  useEffect(() => {
    if (isPending || !session) return

    const shouldRedirect = shouldRedirectOperatorToSetup({
      isAdmin,
      currentPath: location.pathname,
      onboardingError: Boolean(onboardingError),
      onboardingLoading: isOnboardingLoading,
      launchState,
    })

    if (!shouldRedirect) return

    router.navigate({ to: '/setup', replace: true })
  }, [
    isPending,
    session,
    isAdmin,
    isOnboardingLoading,
    onboardingError,
    launchState,
    location.pathname,
    router,
  ])

  if (isPending) {
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <div className="app-premium-panel rounded-[6px] p-8">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
        </div>
      </div>
    )
  }

  if (!session) {
    return null
  }

  if (isSetupRoute && !isAdmin) {
    return (
      <SignedIn>
        <Outlet />
      </SignedIn>
    )
  }

  return (
    <SignedIn>
      <AppLayout>
        <Outlet />
      </AppLayout>
    </SignedIn>
  )
}
